import React, { useEffect, useMemo } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { usePayment } from '../contexts/PaymentContext';
import { Order } from '../types';
import { ArrowLeftIcon, CheckCircleIcon, ClockIcon, XCircleIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import AIChatButton from '../components/AIChatButton';

const OrderDetails: React.FC = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const { getOrderById, getUserOrders, isLoading } = usePayment();

  const order = useMemo(() => (orderId ? getOrderById(orderId) : undefined), [orderId, getOrderById]);

  useEffect(() => {
    if (!order) {
      getUserOrders();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId]);

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('en-GH', {
      style: 'currency',
      currency: 'GHS',
      minimumFractionDigits: 2,
    }).format(price);
  };

  const formatDate = (date?: Date) => {
    if (!date) return '—';
    return new Intl.DateTimeFormat('en-GH', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(date);
  };

  const StatusIcon = ({ status }: { status: Order['status'] }) => {
    switch (status) {
      case 'completed':
        return <CheckCircleIcon className="h-5 w-5 text-green-500" />;
      case 'pending':
        return <ClockIcon className="h-5 w-5 text-yellow-500" />;
      case 'processing':
        return <ClockIcon className="h-5 w-5 text-blue-500" />;
      case 'failed':
        return <XCircleIcon className="h-5 w-5 text-red-500" />;
      case 'cancelled':
        return <ExclamationTriangleIcon className="h-5 w-5 text-gray-500" />;
      default:
        return <ClockIcon className="h-5 w-5 text-gray-500" />;
    }
  };

  if (isLoading && !order) {
    return (
      <div className="p-4 sm:p-6 lg:p-8">
        <div className="h-6 bg-gray-200 rounded w-1/4 mb-4" />
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="animate-pulse space-y-4">
            <div className="h-5 bg-gray-200 rounded w-1/3" />
            <div className="h-5 bg-gray-200 rounded w-1/2" />
            <div className="h-40 bg-gray-200 rounded" />
          </div>
        </div>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="p-4 sm:p-6 lg:p-8">
        <button onClick={() => navigate(-1)} className="inline-flex items-center text-sm text-gray-600 hover:text-gray-800 mb-4">
          <ArrowLeftIcon className="h-4 w-4 mr-1" /> Back
        </button>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 text-center text-gray-600">
          Order not found.
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 lg:p-8">
      <button onClick={() => navigate(-1)} className="inline-flex items-center text-sm text-gray-600 hover:text-gray-800 mb-4">
        <ArrowLeftIcon className="h-4 w-4 mr-1" /> Back
      </button>

      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Order #{order.id}</h1>
        <p className="text-gray-600">Placed on {formatDate(order.createdAt)}</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <StatusIcon status={order.status} />
                <span className="text-sm font-medium text-gray-900 capitalize">{order.status}</span>
              </div>
              <div className="text-sm text-gray-600">Last updated: {formatDate(order.updatedAt)}</div>
            </div>

            <div className="px-6 py-4 divide-y divide-gray-100">
              {order.items.map((item) => (
                <div key={item.id} className="py-3 flex items-center">
                  {item.image ? (
                    <img
                      src={item.image}
                      alt={item.name}
                      className="w-12 h-12 rounded object-cover mr-3"
                      loading="lazy"
                      decoding="async"
                      width={48}
                      height={48}
                      sizes="48px"
                      fetchPriority="low"
                      referrerPolicy="no-referrer"
                    />
                  ) : (
                    <div className="w-12 h-12 rounded bg-gray-100 mr-3" />
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">{item.name}</p>
                    <p className="text-xs text-gray-500 capitalize">{item.type}</p>
                  </div>
                  <div className="text-sm font-medium text-gray-900">{formatPrice(item.amount)}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h3 className="text-sm font-semibold text-gray-900 mb-4">Shipping Address</h3>
            <div className="text-sm text-gray-700 space-y-0.5">
              <div>{order.shippingAddress.firstName} {order.shippingAddress.lastName}</div>
              <div>{order.shippingAddress.address}</div>
              <div>{order.shippingAddress.city}, {order.shippingAddress.state}, {order.shippingAddress.country}</div>
              <div>{order.shippingAddress.postalCode}</div>
            </div>
          </div>
        </div>

        <div className="space-y-6">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h3 className="text-sm font-semibold text-gray-900 mb-4">Order Summary</h3>
            <div className="space-y-3 text-sm">
              <div className="flex justify-between"><span>Items</span><span>{order.items.length}</span></div>
              <div className="flex justify-between"><span>Total</span><span className="font-medium">{formatPrice(order.total)}</span></div>
            </div>
            <div className="mt-4 text-xs text-gray-500">Currency: GHS</div>
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h3 className="text-sm font-semibold text-gray-900 mb-2">Payment</h3>
            <div className="text-sm text-gray-700">
              <div className="flex justify-between"><span>Method</span><span className="capitalize">{order.paymentMethod}</span></div>
              {order.paymentReference && (
                <div className="flex justify-between"><span>Reference</span><span className="break-all">{order.paymentReference}</span></div>
              )}
            </div>
            {order.status === 'completed' && (
              <Link to="/app/my-courses" className="inline-flex items-center mt-4 px-3 py-1.5 rounded text-xs font-medium text-white bg-primary-600 hover:bg-primary-700">
                Access Course
              </Link>
            )}
          </div>
        </div>
      </div>
      {/* AI Chat Button */}
      <AIChatButton />
    </div>
  );
};

export default OrderDetails;


